import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { INTAKE_COLORS, INTAKE_FONT } from "../../theme/intakeTokens";
import { APP_HEADER_HEIGHT } from "../layout/layoutConstants";
import { effectiveUnlockedGateIndex } from "../../config/productFeatures";

const GATES = ["GATE-0", "GATE-1", "GATE-2", "GATE-3", "GATE-4"];
const BTN_SHADOW = "0px 1px 2px 0px #0000000A";
const BTN_INSET = "inset 0px 1px 4px 0px rgba(0, 0, 0, 0.5)";
const VIEW_SUMMARY_BG_DEFAULT =
  "linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(0deg, #005BF0, #005BF0)";
const VIEW_SUMMARY_BG_HOVER =
  "linear-gradient(180deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.08) 100%), linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(0deg, #005BF0, #005BF0)";
const VIEW_SUMMARY_BG_ACTIVE =
  "linear-gradient(180deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.08) 100%), linear-gradient(180deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.08) 100%), linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(0deg, #005BF0, #005BF0)";

const gateActionBtnTextSx = {
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontSize: 13,
  lineHeight: "24px",
  letterSpacing: 0,
  textTransform: "none",
};

const backBtnSx = {
  ...gateActionBtnTextSx,
  width: "fit-content",
  height: 32,
  minHeight: 32,
  px: "16px",
  py: "4px",
  gap: "4px",
  boxSizing: "border-box",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "4px",
  border: "1px solid #005BF0",
  bgcolor: "#FFFFFF",
  color: "#005BF0",
  boxShadow: BTN_SHADOW,
  cursor: "pointer",
  whiteSpace: "nowrap",
  "&:hover:not(:disabled):not(:focus):not(:focus-visible):not(:active)": {
    bgcolor: "#F0F6FF",
    border: "1px solid #005BF0",
    color: "#005BF0",
    boxShadow: BTN_SHADOW,
  },
  "&:focus:not(:disabled):not(:active), &:focus-visible:not(:disabled):not(:active)": {
    outline: "none",
    bgcolor: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    backgroundImage: "none !important",
    border: "2px solid #005BF0",
    color: "#005BF0",
    boxShadow: BTN_SHADOW,
  },
  "&:hover:focus:not(:disabled):not(:active), &:hover:focus-visible:not(:disabled):not(:active)": {
    bgcolor: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    backgroundImage: "none !important",
    border: "2px solid #005BF0",
    color: "#005BF0",
  },
  "&:active:not(:disabled)": {
    bgcolor: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    backgroundImage: "none !important",
    border: "1px solid #005BF0",
    color: "#005BF0",
    boxShadow: BTN_INSET,
  },
  "&:disabled": {
    bgcolor: "#F5F5F5",
    border: "1px solid #E6E6E6",
    color: "#BBBBBB",
    boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)",
    cursor: "not-allowed",
  },
};

const viewSummaryBtnSx = {
  ...gateActionBtnTextSx,
  width: "fit-content",
  height: 32,
  minHeight: 32,
  px: "16px",
  py: "4px",
  gap: "4px",
  boxSizing: "border-box",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "4px",
  border: "1px solid transparent",
  boxShadow: BTN_SHADOW,
  color: "#FFFFFF",
  backgroundImage: VIEW_SUMMARY_BG_DEFAULT,
  cursor: "pointer",
  whiteSpace: "nowrap",
  "&:hover:not(:disabled):not(:focus):not(:focus-visible):not(:active)": {
    backgroundImage: VIEW_SUMMARY_BG_HOVER,
    boxShadow: BTN_SHADOW,
  },
  "&:focus:not(:disabled):not(:active), &:focus-visible:not(:disabled):not(:active)": {
    outline: "none",
    color: "#FFFFFF",
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage: `${VIEW_SUMMARY_BG_DEFAULT} !important`,
    border: "2px solid #005FCC",
    boxShadow: BTN_SHADOW,
  },
  "&:hover:focus:not(:disabled):not(:active), &:hover:focus-visible:not(:disabled):not(:active)": {
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage: `${VIEW_SUMMARY_BG_DEFAULT} !important`,
    border: "2px solid #005FCC",
  },
  "&:active:not(:disabled)": {
    color: "#FFFFFF",
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage: `${VIEW_SUMMARY_BG_ACTIVE} !important`,
    border: "1px solid transparent",
    boxShadow: BTN_INSET,
  },
  "&:disabled": {
    bgcolor: "#F5F5F5",
    color: "#BBBBBB",
    border: "1px solid #E6E6E6",
    backgroundImage: "none",
    boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)",
    opacity: 1,
    cursor: "not-allowed",
  },
  "&:disabled:hover": {
    bgcolor: "#F5F5F5",
    color: "#BBBBBB",
    border: "1px solid #E6E6E6",
    backgroundImage: "none",
  },
};

const GATE_TAB = {
  width: { xs: 80, md: 93 },
  height: APP_HEADER_HEIGHT,
  gap: 16,
  fontFamily: "Lato, sans-serif",
  fontSize: { xs: 13, md: 14 },
  lineHeight: "18px",
  letterSpacing: "1px",
  inactive: { fontWeight: 400, color: "#555555" },
  active: { fontWeight: 700, color: "#555555" },
  disabled: { fontWeight: 400, color: "#BBBBBB" },
};

function gateTabState(index, activeIndex, unlockedUpTo) {
  if (index === activeIndex) return "active";
  if (index <= unlockedUpTo) return "inactive";  // clickable but not currently active
  return "disabled";
}

function GateTab({ label, state, onClick }) {
  const tabStyle = GATE_TAB[state];
  const isDisabled = state === "disabled";
  const isActive = state === "active";
  const clickable = !isDisabled && state !== "active";

  return (
    <Box
      component="button"
      type="button"
      disabled={isDisabled}
      onClick={clickable ? onClick : undefined}
      sx={{
        position: "relative",
        width: GATE_TAB.width,
        minWidth: GATE_TAB.width,
        maxWidth: GATE_TAB.width,
        height: "100%",
        maxHeight: GATE_TAB.height,
        px: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: 0,
        // Padding rather than a transparent border so the active underline can
        // anchor to the row's divider while the label stays centered.
        pb: "2px",
        bgcolor: "transparent",
        cursor: isDisabled ? "default" : "pointer",
        flexShrink: 0,
        boxSizing: "border-box",
        ...(isActive && {
          "&::after": {
            content: '""',
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: "3px",
            bgcolor: "#005BF0",
            pointerEvents: "none",
          },
        }),
      }}
    >
      <Typography
        sx={{
          fontFamily: GATE_TAB.fontFamily,
          fontSize: GATE_TAB.fontSize,
          lineHeight: GATE_TAB.lineHeight,
          letterSpacing: GATE_TAB.letterSpacing,
          textTransform: "uppercase",
          textAlign: "center",
          fontWeight: tabStyle.fontWeight,
          color: tabStyle.color,
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

export default function IntakeGateTabsBar({
  activeGate = 0,
  onBack,
  onViewSummary,
  summaryDisabled = true,
  showActions = true,
  showBack = true,
  showViewSummary = true,
  viewSummaryLabel = "View summary",
  onExportSummary,
  exportDisabled = false,
  exportLabel = "Export Summary",
  showExportSummary = false,
  // The highest gate index the user is allowed to switch to. Defaults to the
  // active gate so tabs are read-only unless the parent opts in.
  unlockedUpTo = activeGate,
  // Optional click handler — receives gate index. If omitted, tabs are visual only.
  onGateClick,
}) {
  const navigate = useNavigate();
  const effectiveUnlockedUpTo = effectiveUnlockedGateIndex(unlockedUpTo);

  const handleBack = () => {
    if (onBack) onBack();
    else navigate(-1);
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "stretch",
        justifyContent: showActions ? "space-between" : "flex-start",
        pl: { xs: 0.5, md: 1 },
        pr: { xs: 2, md: 2.5 },
        height: GATE_TAB.height,
        minHeight: GATE_TAB.height,
        bgcolor: INTAKE_COLORS.surface,
        borderBottom: `1px solid ${INTAKE_COLORS.border}`,
        fontFamily: INTAKE_FONT,
        flexShrink: 0,
        overflow: "visible",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "stretch",
          height: GATE_TAB.height,
          gap: `${GATE_TAB.gap}px`,
          overflow: "visible",
        }}
      >
        {GATES.map((label, i) => (
          <GateTab
            key={label}
            label={label}
            state={gateTabState(i, activeGate, effectiveUnlockedUpTo)}
            onClick={onGateClick ? () => onGateClick(i) : undefined}
          />
        ))}
      </Box>

      {showActions && (
        <Box sx={{ display: "flex", alignItems: "center", gap: "16px", height: "100%" }}>
          {showBack && (
            <Box component="button" type="button" onClick={handleBack} sx={backBtnSx}>
              Back
            </Box>
          )}
          {showExportSummary && (
            <Box
              component="button"
              type="button"
              onClick={onExportSummary}
              disabled={exportDisabled}
              sx={backBtnSx}
            >
              {exportLabel}
            </Box>
          )}
          {showViewSummary && (
          <Box
            component="button"
            type="button"
            onClick={onViewSummary}
            disabled={summaryDisabled}
            sx={viewSummaryBtnSx}
          >
            {viewSummaryLabel}
          </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
